/**
 * stepped-cart.js
 * Stepper logic for the stepped cart page.
 * Handles: step navigation, megaBreakdown initialization, +/- quantity,
 * order summary updates, validation, checkout submission,
 * cookie persistence, URL step tracking, and subscription management.
 */
(function () {
  'use strict';

  var COOKIE_NAME = 'steppedCartSelections';
  var COOKIE_DAYS = 7;

  var SteppedCart = {
    currentStep: 1,
    totalSteps: 0,
    megaBreakdown: {},
    stepEvents: [],
    ticketLimit: 10,
    hasSubscription: false,
    Init: function (config) {

      this.config = config || {};
      this.ticketLimit = config.ticketLimit || 10;
      this.megaProducts = config.megaProducts || [];
      this.cartItems = config.cartItems || [];
      this.addonData = config.addonData || [];
      this.bundleInfo = config.bundleInfo || {};
      this.mainEventId = config.mainEventId || 0;
      this.megaCollectionProducts = config.megaCollectionProducts || [];
      this.subscriptionEnabled = config.subscriptionEnabled || false;
      this.ccFeePercentage = config.ccFeePercentage || 0;
      this.ccFlatFee = config.ccFlatFee || 0;
      this.displayAllProducts = config.displayAllProducts !== false;

      // Customer already bought main-event tickets in a previous order.
      this.hasMainEventOrder = config.mainEventOrderInformation != null && config.mainEventOrderInformation !== '';

      this.InitMegaBreakdown();
      this.RestoreFromCookie();
      this.BuildSteps();
      this.BindEvents();

      // Determine initial step from URL or default to 1
      var initialStep = this.GetStepFromURL() || 1;
      if (initialStep > this.totalSteps) initialStep = 1;
      if (this.hasMainEventOrder && initialStep === 1) initialStep = Math.min(2, this.totalSteps);
      this.GoToStep(initialStep);
      this.UpdateFooterSummary();
    },

    InitMegaBreakdown: function () {
      var breakdown = {};
      var cartBreakdownInput = document.getElementById('hid_cart-breakdown');
      var cartBreakdownObj = {};

      if (cartBreakdownInput && cartBreakdownInput.value) {
        try {
          cartBreakdownObj = JSON.parse(cartBreakdownInput.value);
        } catch (e) {
          console.error('Error parsing cart breakdown:', e);
        }
      }

      var mainEventId = String(this.mainEventId);
      breakdown[mainEventId] = {};
      breakdown['bundle'] = {};

      // Add addon events
      var addonRequiresMain = {};
      if (Array.isArray(this.addonData)) {
        this.addonData.forEach(function (addon) {
          var addonId = String(addon.eventId);
          breakdown[addonId] = {};
          var val = addon.requiresMainTicket;
          addonRequiresMain[addonId] = val !== false && String(val).toLowerCase() !== 'false';
        });
      }

      var isEmptyBreakdown = !cartBreakdownObj || Object.keys(cartBreakdownObj).length === 0;

      this.megaProducts.forEach(function (prod) {
        if (prod.handle === 'subscription') return;

        var variantId = prod.variants[0].id;
        var price = prod.price / 100;
        var ppId = 0;
        var quantity = 0;
        var productEventId = mainEventId;
        var tag = '';
        var isBundle = false;

        if (prod.tags && prod.tags.length > 0) {
          tag = prod.tags[0];
          var tagLower = tag.toLowerCase().trim();

          if (tagLower.indexOf('bundle') === 0) {
            // Bundle product — tag format: "bundle - {productId}"
            isBundle = true;
            productEventId = 'bundle';
          } else {
            var tagParts = tag.split('-');
            if (tagParts.length > 1) {
              productEventId = tagParts[0].trim();
              var ppIdMatch = tag.match(/\((\d+)\)/);
              if (ppIdMatch && ppIdMatch.length > 1) {
                ppId = parseInt(ppIdMatch[1]);
              }
            }
          }
        }

        // Get current quantity from cart
        if (isEmptyBreakdown) {
          for (var i = 0; i < SteppedCart.cartItems.length; i++) {
            if (SteppedCart.cartItems[i].variant_id === variantId) {
              quantity = SteppedCart.cartItems[i].quantity;
              break;
            }
          }
        } else {
          quantity = cartBreakdownObj[tag] || 0;
        }

        if (!breakdown[productEventId]) {
          breakdown[productEventId] = {};
        }

        breakdown[productEventId][prod.title] = {
          variant_id: variantId,
          img_url: prod.featured_image,
          description: prod.description || '',
          tag: tag,
          ppId: ppId,
          price: price,
          quantity: quantity,
          isBundle: isBundle,
          requires_main_ticket: false,
          hasSellingPlan: prod.selling_plan_groups && prod.selling_plan_groups.length > 0,
          sku: (prod.variants && prod.variants[0]) ? prod.variants[0].sku || '' : ''
        };
      });

      // Remove bundle key if no bundle products found
      if (Object.keys(breakdown['bundle']).length === 0) {
        delete breakdown['bundle'];
      }

      this.megaBreakdown = breakdown;

      // Build ordered step events list
      this.stepEvents = [];
      this.stepEvents.push({ eventId: String(mainEventId), isMain: true });
      if (Array.isArray(this.addonData)) {
        this.addonData.forEach(function (addon) {
          SteppedCart.stepEvents.push({
            eventId: String(addon.eventId),
            isMain: false,
            title: addon.eventTitle || '',
            prize: addon.prizeLocalized || addon.jackpotLocalized || ''
          });
        });
      }
      // Last step is summary
      this.totalSteps = this.stepEvents.length + 1;
    },

    BuildSteps: function () {
      var self = this;
      var bundleProducts = this.megaBreakdown['bundle'];
      var hasBundles = bundleProducts && Object.keys(bundleProducts).length > 0;
      var bundleDisplay = this.config.bundleDisplay || 'step1'; // 'step1' or 'all'

      // Build ticket cards for each event step
      this.stepEvents.forEach(function (stepEvent, idx) {
        var stepNum = idx + 1;
        var container = document.querySelector('.stepped-cart-step[data-step="' + stepNum + '"] .ticket-cards-container');
        var eventProducts = self.megaBreakdown[stepEvent.eventId];
        if (!container) return;
        if (!eventProducts || Object.keys(eventProducts).length === 0) return;

        // Section header for event-only tickets
        var eventHeader = document.createElement('div');
        eventHeader.className = 'ticket-section-header super-title super-title--extra-small blue text-shadow-none h2';
        eventHeader.textContent = (self.config.ticketsOnlyLabel || '{EVENT} TICKETS ONLY').replace('{EVENT}', (stepEvent.isMain ? (self.config.mainEventLabel || 'MAIN EVENT') : (stepEvent.title || 'ADD-ON')).toUpperCase());
        container.appendChild(eventHeader);

        // Render event product cards
        self.RenderTicketGrid(container, eventProducts, stepEvent.eventId);

        // Render bundle section if applicable
        var showBundles = hasBundles && (bundleDisplay === 'all' || (bundleDisplay === 'step1' && stepNum === 1));
        if (showBundles) {
          // Bundle section header
          var bundleHeader = document.createElement('div');
          bundleHeader.className = 'ticket-section-header super-title super-title--extra-small blue text-shadow-none h2';
          bundleHeader.textContent = self.config.bundleSectionLabel || 'BUNDLE PACKAGES';
          container.appendChild(bundleHeader);

          // Bundle info text
          if (self.config.bundleInfoText) {
            var bundleInfo = document.createElement('div');
            bundleInfo.className = 'ticket-section-info';
            bundleInfo.textContent = self.config.bundleInfoText;
            container.appendChild(bundleInfo);
          }

          // Render bundle cards
          self.RenderTicketGrid(container, bundleProducts, 'bundle');
        }
      });
    },

    RenderTicketGrid: function (container, products, eventId) {
      var self = this;
      var isBundle = eventId === 'bundle';
      var useImages = self.config.displayProductImages === true;
      var productsArray = Object.entries(products).map(function (entry) {
        return Object.assign({ title: entry[0] }, entry[1]);
      });

      productsArray.sort(function (a, b) { return a.price - b.price; });

      var grid = document.createElement('div');
      grid.className = 'ticket-cards-grid' + (isBundle ? ' bundle-grid' : '') + (useImages ? ' image-grid' : '');

      productsArray.forEach(function (product, pIdx) {
        var card = document.createElement('div');
        card.className = 'ticket-card';
        if (product.isBundle) card.classList.add('ticket-card-bundle');
        if (useImages && product.img_url) card.classList.add('ticket-card-image-mode');
        card.setAttribute('data-variant-id', product.variant_id);
        card.setAttribute('data-tag', product.tag);
        card.setAttribute('data-event-id', eventId);

        if (product.quantity > 0) {
          card.classList.add('has-items');
        }

        // Badge (most popular / best value based on position)
        var badgeHtml = '';
        var totalProducts = productsArray.length;
        if (totalProducts >= 3 && pIdx === Math.floor(totalProducts * 0.5)) {
          badgeHtml = '<div class="ticket-card-badge popular">' + (self.config.mostPopularLabel || 'MOST POPULAR') + '</div>';
        } else if (pIdx === totalProducts - 1 && totalProducts > 1) {
          badgeHtml = '<div class="ticket-card-badge best-value">' + (self.config.bestValueLabel || 'BEST VALUE') + '</div>';
        }

        var priceDisplay = self.FormatMoney(product.price);

        var cardHtml = badgeHtml;

        if (useImages && product.img_url) {
          // Image mode: show product image for both regular and bundle cards
          cardHtml +=
            '<img src="' + product.img_url + '" alt="' + self.escapeHtml(product.title) + '" class="ticket-card-img" loading="lazy">';
        } else if (product.isBundle) {
          // Bundle card: title + price row, description breakdown, tooltip, qty controls
          var descHtml = '';
          if (product.description) {
            descHtml = product.description;
          }

          // Tooltip with full description
          cardHtml +=
            '<div class="bundle-tooltip-wrap" tabindex="0">' +
            '  <div class="bundle-tooltip-icon">i</div>' +
            '  <div class="bundle-tooltip-content blue">' + (descHtml || self.escapeHtml(product.title)) + '</div>' +
            '</div>';

          var bundleParts = self.GetBundleParts(product, pIdx);
          var bundleLabelHtml = bundleParts
            ? bundleParts.main + '<span class="bundle-part-addon"> + ' + bundleParts.addon + '</span><p>Lottery + 50/50 Tickets</p>'
            : self.escapeHtml(product.title);

          cardHtml +=
            '<div class="ticket-card-label one-whole blue"><p>Starter</p>' + bundleLabelHtml + '</div>' +
            '<div class="ticket-card-price one-whole blue">' + priceDisplay + '</div>';

          // Show description breakdown inline
          if (descHtml) {
            cardHtml += '<div class="bundle-description blue">' + descHtml + '</div>';
          }
        } else {
          // Default: title centered, price centered
          var cardLabel = self.GetPackLabel(product.title);
          var ticketsPerPack = self.GetPackTicketCount(product.title);
          var perTicketDisplay = self.FormatMoney(product.price / ticketsPerPack);
          cardHtml +=
            '<div class="ticket-card-label one-whole blue"><p>Starter</p>' + self.escapeHtml(cardLabel) + '<p>Lottery Tickets</p></div>' +
            '<div class="ticket-card-price one-whole blue">' + priceDisplay + '<p class="margin-top-5">' + perTicketDisplay + ' Per Ticket</p></div>';
        }

        cardHtml +=
          '<div class="ticket-card-qty-control one-whole">' +
          '  <button type="button" class="qty-btn qty-minus" data-variant-id="' + product.variant_id + '" data-event-id="' + eventId + '" data-title="' + self.escapeHtml(product.title) + '">—</button>' +
          '  <span class="qty-value black" data-variant-id="' + product.variant_id + '">' + (product.quantity || 0) + '</span>' +
          '  <button type="button" class="qty-btn qty-plus" data-variant-id="' + product.variant_id + '" data-event-id="' + eventId + '" data-title="' + self.escapeHtml(product.title) + '">+</button>' +
          '</div>';

        card.innerHTML = cardHtml;
        grid.appendChild(card);
      });

      container.appendChild(grid);
    },

    // Ticket titles are formatted "{count} for ${price}" — cards show only the count.
    GetPackLabel: function (title) {
      var match = String(title || '').match(/^\s*(\d[\d,]*)/);
      return match ? match[1] : title;
    },

    GetPackTicketCount: function (title) {
      var match = String(title || '').match(/^\s*(\d[\d,]*)/);
      var count = match ? parseInt(match[1].replace(/,/g, ''), 10) : 1;
      return count > 0 ? count : 1;
    },

    GetCurrentLang: function () {
      try {
        if (typeof Weglot !== 'undefined' && typeof Weglot.getCurrentLang === 'function') {
          return Weglot.getCurrentLang();
        }
      } catch (e) { /* Weglot not ready */ }
      return (document.documentElement.lang || 'en').split('-')[0].toLowerCase();
    },

    // French locale puts the currency symbol after the amount and uses a comma decimal.
    FormatMoney: function (amount, forceDecimals) {
      var decimals = (!forceDecimals && amount % 1 === 0) ? 0 : 2;
      var value = amount.toFixed(decimals);
      if (this.GetCurrentLang() === 'fr') {
        return value.replace('.', ',') + ' $';
      }
      return '$' + value;
    },

    // Bundle titles don't expose the main/50-50 split, so fall back to a fixed
    // ticket-count table ordered by ascending bundle price.
    BUNDLE_TICKET_SPLITS: [
      { main: 3, addon: 15 },
      { main: 5, addon: 20 },
      { main: 8, addon: 50 }
    ],

    GetBundleParts: function (product, index) {
      return this.BUNDLE_TICKET_SPLITS[index] || null;
    },

    BindEvents: function () {
      var self = this;

      // Plus/Minus buttons
      document.addEventListener('click', function (e) {
        if (e.target.classList.contains('qty-plus')) {
          self.HandleQuantityChange(e.target, 1);
        } else if (e.target.classList.contains('qty-minus')) {
          self.HandleQuantityChange(e.target, -1);
        }
      });

      // Continue button in footer
      var continueBtn = document.getElementById('stepped-cart-continue-btn');
      if (continueBtn) {
        continueBtn.addEventListener('click', function () {
          self.NextStep();
        });
      }

      // Prev button in footer
      var prevBtn = document.getElementById('stepped-cart-prev-btn');
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          self.PrevStep();
        });
      }

      // Step indicators — always clickable (forward and backward)
      var stepIndicators = document.querySelectorAll('.step-indicator');
      stepIndicators.forEach(function (indicator) {
        indicator.addEventListener('click', function () {
          var stepNum = parseInt(indicator.getAttribute('data-step'));
          if (stepNum && stepNum !== self.currentStep) {
            self.GoToStep(stepNum);
          }
        });
      });

      // Eligibility checkboxes
      var checkboxes = document.querySelectorAll('.eligibility-cbx');
      checkboxes.forEach(function (cbx) {
        cbx.addEventListener('change', function () {
          self.UpdateCheckoutStatus();
        });
      });

      // Subscription checkbox
      var subCheckbox = document.getElementById('subscription-checkbox');
      if (subCheckbox) {
        subCheckbox.addEventListener('change', function () {
          self.HandleSubscriptionToggle(subCheckbox);
        });
      }

      // Checkout button
      var checkoutBtn = document.getElementById('checkoutBtn');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', function () {
          self.HandleCheckout();
        });
      }
    },

    HandleQuantityChange: function (btn, delta) {
      var variantId = btn.getAttribute('data-variant-id');
      var eventId = btn.getAttribute('data-event-id');

      // Find product in megaBreakdown
      var eventProducts = this.megaBreakdown[eventId];
      if (!eventProducts) return;

      var product = null;
      var productTitle = null;
      for (var title in eventProducts) {
        if (String(eventProducts[title].variant_id) === String(variantId)) {
          product = eventProducts[title];
          productTitle = title;
          break;
        }
      }
      if (!product) return;

      var newQty = Math.max(0, (product.quantity || 0) + delta);

      // Check ticket limit
      if (delta > 0) {
        var totalTickets = this.GetTotalTicketCount();
        if (totalTickets >= this.ticketLimit) {
          this.ShowStepError(this.currentStep, this.config.ticketLimitError || 'Maximum ticket limit reached.');
          return;
        }
      }

      product.quantity = newQty;
      // Upsell prompt: on step 1, when adding (delta > 0), if not the last product, show once per hour
      if (delta > 0 && this.currentStep == 1 && product.isBundle) {
        this.MaybeShowUpsell(eventId, product);
      }

      // Update ALL UI instances (bundles may appear on multiple steps)
      var qtyDisplays = document.querySelectorAll('.qty-value[data-variant-id="' + variantId + '"]');
      qtyDisplays.forEach(function (el) {
        el.textContent = newQty;
      });

      // Update ALL card instances
      var cards = document.querySelectorAll('.ticket-card[data-variant-id="' + variantId + '"]');
      cards.forEach(function (card) {
        if (newQty > 0) {
          card.classList.add('has-items');
        } else {
          card.classList.remove('has-items');
        }
      });

      this.UpdateCartBreakdown();
      this.SaveToCookie();
      this.UpdateFooterSummary();
      this.HideStepError(this.currentStep);
    },

    GetTotalTicketCount: function () {
      var total = 0;
      for (var eventId in this.megaBreakdown) {
        var products = this.megaBreakdown[eventId];
        for (var title in products) {
          total += products[title].quantity || 0;
        }
      }
      return total;
    },

    GetEventTotal: function (eventId) {
      var total = 0;
      var products = this.megaBreakdown[eventId];
      if (!products) return 0;
      for (var title in products) {
        total += (products[title].quantity || 0) * products[title].price;
      }
      return total;
    },

    GetCartTotal: function () {
      var total = 0;
      for (var eventId in this.megaBreakdown) {
        total += this.GetEventTotal(eventId);
      }
      return total;
    },

    GoToStep: function (stepNum) {
      // Hide all steps
      var allSteps = document.querySelectorAll('.stepped-cart-step');
      allSteps.forEach(function (step) {
        step.classList.remove('active');
      });

      // Show target step
      var isSummary = stepNum > this.stepEvents.length;
      var targetStep;
      if (isSummary) {
        targetStep = document.querySelector('[data-step="summary"]');
        this.PopulateSummary();
        this.RunGeofenceCheck();
      } else {
        targetStep = document.querySelector('.stepped-cart-step[data-step="' + stepNum + '"]');
      }
      if (targetStep) {
        targetStep.classList.add('active');
      }

      this.currentStep = stepNum;
      this.UpdateProgressBar();
      this.UpdateFooterVisibility();
      this.UpdateURLStep(stepNum);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    PrevStep: function () {
      if (this.currentStep > 1) {
        this.GoToStep(this.currentStep - 1);
      }
    },

    NextStep: function () {
      // Validate current step before proceeding
      if (!this.ValidateCurrentStep()) return;

      if (this.currentStep < this.totalSteps) {
        this.GoToStep(this.currentStep + 1);
      }
    },

    ValidateCurrentStep: function () {
      // On main event step (step 1), require at least one item selected (main OR bundle)
      if (this.currentStep === 1 && !this.hasMainEventOrder) {
        var mainEventId = this.stepEvents[0].eventId;
        var mainTotal = this.GetEventTotal(mainEventId);
        var bundleTotal = this.GetEventTotal('bundle');
        if (mainTotal + bundleTotal <= 0) {
          this.ShowStepError(1, this.config.selectPackageError || 'Select a package above to continue');
          return false;
        }
      }

      // Addon steps: no requirement (optional)
      return true;
    },

    UpdateProgressBar: function () {
      var self = this;
      var indicators = document.querySelectorAll('.step-indicator');
      var connectors = document.querySelectorAll('.step-connector');

      indicators.forEach(function (indicator, idx) {
        var stepNum = idx + 1;
        indicator.classList.remove('active', 'completed');

        if (stepNum === self.currentStep) {
          indicator.classList.add('active');
        } else if (stepNum < self.currentStep) {
          indicator.classList.add('completed');
        }
      });

      connectors.forEach(function (connector, idx) {
        connector.classList.remove('completed');
        if (idx + 1 < self.currentStep) {
          connector.classList.add('completed');
        }
      });
    },

    UpdateFooterVisibility: function () {
      var footer = document.querySelector('.stepped-cart-footer');
      if (!footer) return;

      // Hide footer on summary step (checkout button is in the summary)
      if (this.currentStep > this.stepEvents.length) {
        footer.style.display = 'none';
      } else {
        footer.style.display = '';
      }

      // Show/hide prev button
      var prevBtn = document.getElementById('stepped-cart-prev-btn');
      if (prevBtn) {
        prevBtn.style.display = this.currentStep > 1 ? '' : 'none';
      }
    },

    UpdateFooterSummary: function () {
      var self = this;
      var summaryItems = document.querySelector('.footer-summary-items');
      if (!summaryItems) return;

      summaryItems.innerHTML = '';

      this.stepEvents.forEach(function (stepEvent, idx) {
        var eventTotal = self.GetEventTotal(stepEvent.eventId);
        var eventLabel = stepEvent.isMain ? (self.config.mainEventLabel || 'Main Event') : (stepEvent.title || 'Add-on');

        // Count tickets for this event
        var ticketCount = 0;
        var products = self.megaBreakdown[stepEvent.eventId];
        if (products) {
          for (var title in products) {
            if (products[title].quantity > 0) {
              var match = title.match(/^(\d+)/);
              var perPack = match ? parseInt(match[1]) : 1;
              ticketCount += products[title].quantity * perPack;
            }
          }
        }

        var displayValue = eventTotal > 0 ? ticketCount + ' tickets — ' + self.FormatMoney(eventTotal, true) : 'None selected';

        var item = document.createElement('div');
        item.className = 'footer-summary-item';
        item.innerHTML =
          '<span class="footer-summary-item-label">' + self.escapeHtml(eventLabel) + '</span>' +
          '<span class="footer-summary-item-value blue">' + displayValue + '</span>';
        summaryItems.appendChild(item);
      });

      // Add bundle summary if bundles exist and have items
      var bundleTotal = this.GetEventTotal('bundle');
      if (bundleTotal > 0) {
        var bundleItem = document.createElement('div');
        bundleItem.className = 'footer-summary-item';
        bundleItem.innerHTML =
          '<span class="footer-summary-item-label">' + self.escapeHtml(self.config.bundleLabel || 'Bundles') + '</span>' +
          '<span class="footer-summary-item-value blue">' + self.FormatMoney(bundleTotal, true) + '</span>';
        summaryItems.appendChild(bundleItem);
      }

      // Update total (includes bundles via GetCartTotal)
      var totalEl = document.getElementById('footer-total-value');
      if (totalEl) {
        totalEl.textContent = this.FormatMoney(this.GetCartTotal(), true);
      }

      // Update continue button state — bundles count as valid for step 1
      var continueBtn = document.getElementById('stepped-cart-continue-btn');
      if (continueBtn) {
        var mainEventId = this.stepEvents[0].eventId;
        var mainTotal = this.GetEventTotal(mainEventId);
        var bundleT = this.GetEventTotal('bundle');
        if (this.currentStep === 1 && !this.hasMainEventOrder && (mainTotal + bundleT) <= 0) {
          continueBtn.disabled = true;
        } else {
          continueBtn.disabled = false;
        }
      }

      // Update footer message
      var footerMsg = document.getElementById('footer-message');
      if (footerMsg) {
        var mainEventId = this.stepEvents[0].eventId;
        var mainTotal = this.GetEventTotal(mainEventId);
        var bundleT2 = this.GetEventTotal('bundle');
        if (this.currentStep === 1 && !this.hasMainEventOrder && (mainTotal + bundleT2) <= 0) {
          footerMsg.textContent = this.config.selectPackageError || 'Select a package above to continue';
          footerMsg.style.display = 'block';
        } else {
          footerMsg.style.display = 'none';
        }
      }
    },

    PopulateSummary: function () {
      var self = this;
      var container = document.getElementById('summary-order-items');
      if (!container) return;

      container.innerHTML = '';

      // All event groups including bundles
      var allGroups = this.stepEvents.slice();
      if (this.megaBreakdown['bundle'] && Object.keys(this.megaBreakdown['bundle']).length > 0) {
        allGroups.push({ eventId: 'bundle', isMain: false, isBundle: true, title: this.config.bundleLabel || 'Bundles' });
      }

      allGroups.forEach(function (stepEvent) {
        var products = self.megaBreakdown[stepEvent.eventId];
        if (!products) return;

        for (var title in products) {
          var product = products[title];
          if (product.quantity <= 0) continue;

          var match = title.match(/^(\d+)/);
          var ticketCount = match ? parseInt(match[1]) * product.quantity : product.quantity;
          var lineTotal = product.price * product.quantity;
          var eventLabel = stepEvent.isBundle ? (self.config.bundleLabel || 'Bundle') : (stepEvent.isMain ? (self.config.mainEventLabel || 'Main Event') : (stepEvent.title || 'Add-on'));

          var itemEl = document.createElement('div');
          itemEl.className = 'summary-order-item';
          itemEl.innerHTML =
            '<div>' +
            '  <div class="summary-order-item-title">' + ticketCount + ' ' + self.escapeHtml(eventLabel) + ' tickets</div>' +
            '  <div class="summary-order-item-desc">' + self.escapeHtml(title) + '</div>' +
            '</div>' +
            '<div class="summary-order-item-price">' + self.FormatMoney(lineTotal, true) + '</div>';
          container.appendChild(itemEl);
        }
      });

      // Total
      var totalEl = document.getElementById('summary-total-price');
      if (totalEl) {
        totalEl.textContent = this.FormatMoney(this.GetCartTotal(), true);
      }

      this.UpdateCheckoutStatus();
    },

    RunGeofenceCheck: function () {
      var self = this;
      if (this._geoCheckDone) return;

      var isEligible = this.readCookie('isEligible') === 'true';
      if (isEligible) {
        // Already passed geofence — refresh location from cookies
        if (navigator.geolocation && typeof HandleCookieLookupSuccess === 'function') {
          navigator.geolocation.getCurrentPosition(
            function (pos) { HandleCookieLookupSuccess(pos); self.UpdateCheckoutStatus(); },
            function () { if (typeof HandleCookieLookupError === 'function') HandleCookieLookupError(); self.UpdateCheckoutStatus(); }
          );
        }
        this._geoCheckDone = true;
        return;
      }

      // Not yet eligible — run the full geofence check
      var config = window.geofenceConfig || {};
      if (typeof RequestLocation === 'function') {
        // Override updateCheckoutBtnStatus so general-cart.js calls back into stepped cart
        var origUpdate = window.updateCheckoutBtnStatus;
        window.updateCheckoutBtnStatus = function () {
          self.UpdateCheckoutStatus();
          if (typeof origUpdate === 'function') origUpdate();
        };

        RequestLocation(
          config.areaSelection || '',
          config.multipleJurisdictionSale || false,
          config.additionalJurisdictionLists || ''
        );
        this._geoCheckDone = true;
      }
    },

    UpdateCheckoutStatus: function () {
      var checkoutBtn = document.getElementById('checkoutBtn');
      if (!checkoutBtn) return;

      var cartTotal = this.GetCartTotal();
      var hasItems = cartTotal > 0;

      // Check geo eligibility
      var isEligible = this.readCookie('isEligible') === 'true';

      // Update geo status indicator
      var geoBox = document.getElementById('geo-status-box');
      var geoResult = document.getElementById('geo-status-result');
      if (geoBox && geoResult) {
        if (isEligible) {
          geoBox.style.borderColor = 'var(--color-accent, #4a4)';
          geoBox.style.background = '#f0fff0';
          geoResult.textContent = '✓ Location verified';
          geoResult.style.display = 'block';
          geoResult.style.color = 'var(--color-accent, #2a7)';
        } else {
          geoBox.style.borderColor = '';
          geoBox.style.background = '';
          geoResult.style.display = 'none';
        }
      }

      // Check all eligibility checkboxes
      var allChecked = true;
      var checkboxes = document.querySelectorAll('.eligibility-cbx');
      checkboxes.forEach(function (cbx) {
        if (!cbx.checked) allChecked = false;
      });

      // Check addon-requires-main rule — if any addon has items, main event or bundle must also have items
      var mainEventId = this.stepEvents[0].eventId;
      var hasMainTickets = this.hasMainEventOrder || this.GetEventTotal(mainEventId) > 0 || this.GetEventTotal('bundle') > 0;
      var hasAddonItems = false;
      for (var i = 1; i < this.stepEvents.length; i++) {
        var addonEventId = this.stepEvents[i].eventId;
        if (this.GetEventTotal(addonEventId) > 0) {
          hasAddonItems = true;
          break;
        }
      }
      var megaValid = !hasAddonItems || hasMainTickets;

      // Ticket limit
      var withinLimit = this.GetTotalTicketCount() <= this.ticketLimit;

      var canCheckout = hasItems && isEligible && allChecked && megaValid && withinLimit;
      checkoutBtn.disabled = !canCheckout;

      // Show/hide validation error messages
      var megaError = document.getElementById('mega-validation-error');
      if (megaError) {
        megaError.style.display = (!megaValid) ? 'block' : 'none';
      }

      var validationError = document.getElementById('validation-error');
      if (validationError) {
        validationError.style.display = (!withinLimit) ? 'block' : 'none';
      }

      // Update hint
      var hint = document.getElementById('checkout-hint');
      if (hint) {
        if (!allChecked) {
          hint.textContent = this.config.checkboxHint || 'Please tick all boxes above to continue';
          hint.style.display = 'block';
        } else if (!isEligible) {
          hint.textContent = this.config.geoHint || 'Location verification required';
          hint.style.display = 'block';
        } else if (!megaValid) {
          hint.style.display = 'none';
        } else {
          hint.style.display = 'none';
        }
      }
    },

    HandleSubscriptionToggle: function (checkbox) {
      var self = this;
      var modal = document.getElementById('confirmation-modal');

      if (checkbox.checked && modal) {
        // Show confirmation modal
        checkbox.checked = false;
        var isDesktop = window.innerWidth >= 768;

        if (isDesktop) {
          // Desktop: grey overlay + modal positioned near the subscription checkbox
          modal.style.display = 'flex';
          modal.style.position = 'fixed';
          modal.style.zIndex = '1000';
          modal.style.left = '0';
          modal.style.top = '0';
          modal.style.width = '100%';
          modal.style.height = '100%';
          modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
          modal.style.alignItems = 'flex-start';
          modal.style.justifyContent = 'center';

          // Position modal near the subscription checkbox
          var subSection = document.getElementById('membership-option-id');
          if (subSection) {
            var rect = subSection.getBoundingClientRect();
            var offsetTop = rect.bottom + window.scrollY + 10;
            modal.style.paddingTop = Math.max(0, rect.bottom + 10) + 'px';
          } else {
            modal.style.paddingTop = '20%';
          }
        } else {
          // Mobile: full-screen overlay centered
          modal.style.display = 'flex';
          modal.style.position = 'fixed';
          modal.style.zIndex = '1000';
          modal.style.left = '0';
          modal.style.top = '0';
          modal.style.width = '100%';
          modal.style.height = '100%';
          modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
          modal.style.alignItems = 'center';
          modal.style.justifyContent = 'center';
          modal.style.paddingTop = '0';
        }

        var confirmBtn = document.getElementById('confirm-button');
        var cancelBtn = document.getElementById('cancel-button');

        var confirmHandler = function () {
          checkbox.checked = true;
          self.hasSubscription = true;
          modal.style.display = 'none';
          confirmBtn.removeEventListener('click', confirmHandler);
        };

        var cancelHandler = function () {
          checkbox.checked = false;
          self.hasSubscription = false;
          modal.style.display = 'none';
          cancelBtn.removeEventListener('click', cancelHandler);
        };

        if (confirmBtn) confirmBtn.addEventListener('click', confirmHandler);
        if (cancelBtn) cancelBtn.addEventListener('click', cancelHandler);
      } else {
        this.hasSubscription = checkbox.checked;
      }
    },

        // Build subscription line items from the current cart breakdown, expanding
    // any bundle products into their constituent individual products so each is
    // subscribed to on its own.
    BuildSubscriptionItems: function (cartBreakdown) {
      var megaProducts = this.megaProducts || [];
      var bundleInfo = this.bundleInfo || {};
      var subscriptionItems = [];

      // Direct (non-bundle) products that carry a selling plan and a positive quantity
      var directMatches = megaProducts
        .filter(function (product) {
          return product.selling_plan_groups &&
            product.selling_plan_groups.length >= 0 &&
            Array.isArray(product.tags) &&
            product.tags.some(function (tag) {
              return cartBreakdown[tag] > 0 && tag.indexOf('bundle - ') !== 0;
            });
        })
        .map(function (product) {
          var matchingTag = product.tags.find(function (tag) {
            return cartBreakdown[tag] > 0 && tag.indexOf('bundle - ') !== 0;
          });
          return {
            title: product.title,
            sku: product.variants[0].sku,
            quantity: cartBreakdown[matchingTag],
            price: (product.price / 100).toFixed(2)
          };
        })
        .filter(function (item) { return item.quantity > 0; });

      if (directMatches.length > 0) {
        subscriptionItems = directMatches.slice();
      }

      // Expand each bundle in the cart into its individual products
      var bundleItems = [];
      for (var tag in cartBreakdown) {
        if (tag.indexOf('bundle - ') !== 0) continue;

        var quantity = cartBreakdown[tag];
        if (quantity <= 0) continue;

        var bundleProductId = tag.split('bundle - ')[1];
        if (bundleInfo && bundleInfo[bundleProductId]) {
          bundleInfo[bundleProductId].forEach(function (bundleItem) {
            var totalQuantity = parseInt(bundleItem.quantity, 10) * quantity;
            if (totalQuantity > 0) {
              bundleItems.push({
                title: bundleItem.title,
                sku: bundleItem.sku,
                quantity: totalQuantity,
                price: bundleItem.price
              });
            }
          });
        }
      }

      // Merge bundle-derived items into the direct matches, accumulating quantities
      if (bundleItems.length > 0) {
        var itemMap = new Map();

        subscriptionItems.forEach(function (item) {
          itemMap.set(item.title + '_' + item.sku, item);
        });

        bundleItems.forEach(function (item) {
          var key = item.title + '_' + item.sku;
          if (itemMap.has(key)) {
            itemMap.get(key).quantity += item.quantity;
          } else {
            itemMap.set(key, item);
          }
        });

        subscriptionItems = Array.from(itemMap.values());
      }

      return subscriptionItems.filter(function (item) { return item.quantity > 0; });
    },

    HandleCheckout: async function () {
      var self = this;

      try {
        // Build cart updates
        var updates = {};
        var subscriptionItems = [];
        var cartBreakdown = {};

        for (var eventId in this.megaBreakdown) {
          var products = this.megaBreakdown[eventId];
          for (var title in products) {
            var product = products[title];
            updates[product.variant_id] = product.quantity;
            if (product.tag) {
              cartBreakdown[product.tag] = product.quantity;
            }
          }
        }

        // Build subscription line items, expanding bundles into their individual products
        if (this.hasSubscription) {
          subscriptionItems = this.BuildSubscriptionItems(cartBreakdown);
        }

        // Get geo data
        var latLong = document.getElementById('hid_latitude-longitude');
        var latLongValue = latLong ? latLong.value : '';

        // Get referrer code
        var refCode = document.getElementById('hid_ref-code');
        var refCodeValue = refCode ? refCode.value : '';

        // Items to add (subscription placeholder)
        var itemsToAdd = [];

        // Handle subscription placeholder
        if (this.hasSubscription && subscriptionItems.length > 0 && this.config.subscriptionVariantId) {
          var hasPlaceholder = this.cartItems.some(function (item) {
            return item.handle === 'subscription';
          });
          if (!hasPlaceholder) {
            itemsToAdd.push({
              id: self.config.subscriptionVariantId,
              quantity: 1,
              selling_plan: self.config.subscriptionSellingPlanId || undefined
            });
          }
        }

        // Remove subscription placeholder if not subscribed
        if (!this.hasSubscription || subscriptionItems.length === 0) {
          var subItem = this.cartItems.find(function (item) {
            return item.handle === 'subscription';
          });
          if (subItem) {
            updates[subItem.variant_id] = 0;
          }
        }

        // Add items first if needed
        if (itemsToAdd.length > 0) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });
        }

        // Update cart with items and attributes
        var response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: updates,
            attributes: {
              'LatLong': latLongValue,
              'CartBreakdown': JSON.stringify(cartBreakdown),
              'ReferrerCode': refCodeValue || null,
              'SubscriptionItem': this.hasSubscription && subscriptionItems.length > 0 ? JSON.stringify(subscriptionItems) : null
            }
          })
        });

        if (!response.ok) {
          if (response.status === 422) {
            alert('A product in your cart is no longer available. Please refresh the page.');
          }
          throw new Error('Failed to update cart');
        }

        // Update hidden inputs for consistency
        var subInput = document.getElementById('hid_subscription_items');
        if (subInput) {
          subInput.value = this.hasSubscription && subscriptionItems.length > 0 ? JSON.stringify(subscriptionItems) : '';
        }

        // Clear cookie on successful checkout
        this.clearCookie(COOKIE_NAME);

        // Redirect to checkout
        window.location.href = '/checkout?locale=' + this.GetCurrentLang();
      } catch (error) {
        console.error('Checkout error:', error);
        alert('Something went wrong. Please try again.');
      }
    },

    UpdateCartBreakdown: function () {
      var cartBreakdownInput = document.getElementById('hid_cart-breakdown');
      if (!cartBreakdownInput) return;

      var cartObj = {};
      for (var eventId in this.megaBreakdown) {
        var products = this.megaBreakdown[eventId];
        for (var title in products) {
          var product = products[title];
          if (product.tag) {
            cartObj[product.tag] = product.quantity;
          }
        }
      }
      cartBreakdownInput.value = JSON.stringify(cartObj);
    },

    // ── Cookie Persistence ──

    SaveToCookie: function () {
      var selections = {};
      for (var eventId in this.megaBreakdown) {
        var products = this.megaBreakdown[eventId];
        for (var title in products) {
          var product = products[title];
          if (product.quantity > 0) {
            selections[String(product.variant_id)] = product.quantity;
          }
        }
      }
      this.setCookie(COOKIE_NAME, JSON.stringify(selections), COOKIE_DAYS);
    },

    RestoreFromCookie: function () {
      var saved = this.readCookie(COOKIE_NAME);
      if (!saved) return;

      try {
        var selections = JSON.parse(saved);
        if (!selections || typeof selections !== 'object') return;

        // Apply saved quantities to megaBreakdown
        for (var eventId in this.megaBreakdown) {
          var products = this.megaBreakdown[eventId];
          for (var title in products) {
            var product = products[title];
            var variantKey = String(product.variant_id);
            if (selections[variantKey] !== undefined) {
              product.quantity = parseInt(selections[variantKey]) || 0;
            }
          }
        }

        // Update the hidden cart breakdown input
        this.UpdateCartBreakdown();
      } catch (e) {
        console.error('[stepped-cart] Error restoring cookie:', e);
      }
    },

    // ── URL Step Tracking ──

    GetStepFromURL: function () {
      var params = new URLSearchParams(window.location.search);
      var step = params.get('steps');
      return step ? parseInt(step) : null;
    },

    UpdateURLStep: function (stepNum) {
      var url = new URL(window.location.href);
      url.searchParams.set('steps', stepNum);
      window.history.replaceState({}, '', url.toString());
    },

    // ── Utility Methods ──

    setCookie: function (name, value, days) {
      var expires = '';
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    },

    clearCookie: function (name) {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';
    },

    ShowStepError: function (stepNum, message) {
      var errorEl = document.getElementById('step-error-' + stepNum);
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('visible');
      }
    },

    HideStepError: function (stepNum) {
      var errorEl = document.getElementById('step-error-' + stepNum);
      if (errorEl) {
        errorEl.classList.remove('visible');
      }
    },

    MaybeShowUpsell: function (eventId, currentProduct) {
      // Only fire once — check cookie
      if (this.readCookie('steppedCartUpsellShown')) return;

      // Get non-bundle products for this event, sorted by price
      var eventProducts = this.megaBreakdown[eventId];
      console.log(eventProducts)
      if (!eventProducts) return;
      var sorted = [];
      for (var title in eventProducts) {
        var p = eventProducts[title];
        if (p.isBundle) sorted.push({ title: title, price: p.price, variant_id: p.variant_id });
      }
      sorted.sort(function (a, b) { return a.price - b.price; });

      // Find current product index
      var currentIdx = -1;
      for (var i = 0; i < sorted.length; i++) {
        if (String(sorted[i].variant_id) === String(currentProduct.variant_id)) {
          currentIdx = i;
          break;
        }
      }

      // If it's the last (most expensive) product, don't show
      if (currentIdx === -1 || currentIdx >= sorted.length - 1) return;

      // Get the next higher product
      var nextProduct = sorted[currentIdx + 1];
      var nextPrice = this.FormatMoney(nextProduct.price);

      // Populate modal
      var modalText = document.getElementById('upsell-modal-text');
      var modalProduct = document.getElementById('upsell-modal-product');
      if (modalText) {
        modalText.textContent = 'For just ' + nextPrice + ', you could get the ' + nextProduct.title + ' package instead!';
      }
      // if (modalProduct) {
      //   modalProduct.innerHTML =
      //     '<div class="upsell-product-card">' +
      //     '  <span class="upsell-product-title">' + this.escapeHtml(nextProduct.title) + '</span>' +
      //     '  <span class="upsell-product-price">' + nextPrice + '</span>' +
      //     '</div>';
      // }

      // Show modal
      var overlay = document.getElementById('upsell-modal-overlay');
      if (overlay) overlay.style.display = 'flex';

      // Set cookie (1 hour)
      var expires = new Date(Date.now() + 3600000).toUTCString();
      document.cookie = 'steppedCartUpsellShown=1; expires=' + expires + '; path=/';

      // Bind upgrade action
      var self = this;
      var upgradeBtn = document.getElementById('upsell-modal-upgrade');
      var declineBtn = document.getElementById('upsell-modal-decline');
      var closeBtn = document.getElementById('upsell-modal-close');

      var closeModal = function () {
        if (overlay) overlay.style.display = 'none';
      };

      if (upgradeBtn) {
        upgradeBtn.onclick = function () {
          // Remove current product quantity, add 1 of the upgrade
          self.SetProductQuantity(eventId, currentProduct.variant_id, 0);
          self.SetProductQuantity(eventId, nextProduct.variant_id, 1);
          closeModal();
        };
      }
      if (declineBtn) declineBtn.onclick = closeModal;
      if (closeBtn) closeBtn.onclick = closeModal;
      if (overlay) {
        overlay.onclick = function (e) {
          if (e.target === overlay) closeModal();
        };
      }
    },

    SetProductQuantity: function (eventId, variantId, qty) {
      var eventProducts = this.megaBreakdown[eventId];
      if (!eventProducts) return;
      for (var title in eventProducts) {
        if (String(eventProducts[title].variant_id) === String(variantId)) {
          eventProducts[title].quantity = qty;
          break;
        }
      }
      // Update UI
      var qtyDisplays = document.querySelectorAll('.qty-value[data-variant-id="' + variantId + '"]');
      qtyDisplays.forEach(function (el) { el.textContent = qty; });
      var cards = document.querySelectorAll('.ticket-card[data-variant-id="' + variantId + '"]');
      cards.forEach(function (card) {
        if (qty > 0) { card.classList.add('has-items'); } else { card.classList.remove('has-items'); }
      });
      this.UpdateCartBreakdown();
      this.SaveToCookie();
      this.UpdateFooterSummary();
    },

    readCookie: function (name) {
      var nameEQ = name + '=';
      var ca = document.cookie.split(';');
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i].trim();
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
      }
      return null;
    },

    escapeHtml: function (str) {
      if (!str) return '';
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
  };

  window.SteppedCart = SteppedCart;
})();
